import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../../../components/ui';
import { CaretIcon, EditLineIcon, TrashIcon } from '../../../assets/icons';
import EcHierarchyTree from './EcHierarchyTree';
import AddLevelModal, { type NewLevel, type ParentOption } from './AddLevelModal';
import type { HierarchyView } from './DefineBoundaryStep';

/** A single organization level. `parentId` is null for a top-level (root) node. */
export interface Level {
  id: string;
  name: string;
  parentId: string | null;
}

interface LevelNode extends Level {
  children: LevelNode[];
}

/** Nest the flat level list into a tree, preserving insertion order among siblings. */
function buildTree(levels: Level[]): LevelNode[] {
  const map = new Map<string, LevelNode>();
  levels.forEach((l) => map.set(l.id, { ...l, children: [] }));
  const roots: LevelNode[] = [];
  levels.forEach((l) => {
    const node = map.get(l.id)!;
    if (l.parentId && map.has(l.parentId)) map.get(l.parentId)!.children.push(node);
    else roots.push(node);
  });
  return roots;
}

/** Depth-first walk yielding each node with its tree depth (root = 0). */
function walkTree(nodes: LevelNode[], depth = 0): { node: LevelNode; depth: number }[] {
  return nodes.flatMap((node) => [{ node, depth }, ...walkTree(node.children, depth + 1)]);
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-xl border border-line-soft bg-white p-6 shadow-card">
      {children}
    </div>
  );
}

/** Read-only rendering of the selected-hierarchy path reported by step 2. */
function SelectedHierarchy({ nodes }: { nodes: HierarchyView[] }) {
  const renderNode = (node: HierarchyView) => (
    <div key={node.id}>
      <div
        className={
          node.selected
            ? 'flex items-center gap-2.5 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2'
            : 'flex items-center gap-2.5 px-3 py-2'
        }
      >
        {node.icon ? (
          <node.icon className="size-5 shrink-0 text-brand" />
        ) : (
          <span className="flex size-5 shrink-0 items-center justify-center">
            <span className="size-1.5 rounded-full bg-[#94a3b8]" />
          </span>
        )}
        <span
          className={[
            'text-sm',
            node.selected ? 'font-semibold text-brand' : 'font-medium text-ink',
          ].join(' ')}
        >
          {node.label}
        </span>
        {node.level && (
          <span className="ml-auto rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
            {node.level}
          </span>
        )}
        {node.selected && !node.level && (
          <span className="ml-auto text-[10px] font-bold tracking-[0.5px] text-brand">SELECTED</span>
        )}
      </div>

      {node.sub && <div className="pl-[42px] text-xs text-[#94a3b8]">{node.sub}</div>}

      {node.children.length > 0 && (
        <div className="ml-[19px] flex flex-col gap-1 border-l border-dashed border-[#cbd5e1] pl-3">
          {node.children.map(renderNode)}
        </div>
      )}
    </div>
  );

  return <div className="flex flex-col gap-1">{nodes.map(renderNode)}</div>;
}

/** Step 3 — sequence the organization's administrative levels against the EC hierarchy. */
export default function BuildHierarchyStep({
  selection,
  onLevelsChange,
  onLevelsDataChange,
  initialLevels,
}: {
  selection?: HierarchyView[];
  onLevelsChange?: (levels: string[]) => void;
  onLevelsDataChange?: (levels: Level[]) => void;
  initialLevels?: Level[];
}) {
  const [levels, setLevels] = useState<Level[]>([]);
  const seededRef = useRef(false);

  // Edit/view: seed the tree from the org's saved levels once they arrive.
  useEffect(() => {
    if (seededRef.current) return;
    if (initialLevels && initialLevels.length > 0) {
      seededRef.current = true;
      setLevels(initialLevels);
    }
  }, [initialLevels]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const nextId = useRef(1);

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const tree = useMemo(() => buildTree(levels), [levels]);
  const flat = useMemo(() => walkTree(tree), [tree]);

  // Existing nodes offered as parents in the Add modal (depth drives indentation).
  const parentOptions = useMemo<ParentOption[]>(
    () => flat.map(({ node, depth }) => ({ id: node.id, name: node.name, depth })),
    [flat],
  );

  // Report the ordered level names up (depth-first) so step 4's template can be prefilled.
  useEffect(() => {
    onLevelsChange?.(flat.map(({ node }) => node.name));
  }, [flat, onLevelsChange]);

  // Report the structured levels up so the wizard can POST them on Save.
  useEffect(() => {
    onLevelsDataChange?.(levels);
  }, [levels, onLevelsDataChange]);

  const createLevel = (level: NewLevel) => {
    const id = `l${nextId.current++}`;
    setLevels((ls) => [...ls, { id, name: level.name, parentId: level.parent || null }]);
    setAddOpen(false);
  };

  const startEdit = (level: Level) => {
    setEditingId(level.id);
    setDraft(level.name);
  };

  const saveEdit = () => {
    if (!editingId) return;
    const name = draft.trim();
    setLevels((ls) => ls.map((l) => (l.id === editingId ? { ...l, name: name || l.name } : l)));
    setEditingId(null);
  };

  // Removing a node removes its whole subtree, so no children are left orphaned.
  const deleteLevel = (id: string) => {
    setLevels((ls) => {
      const doomed = new Set([id]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const l of ls) {
          if (l.parentId && doomed.has(l.parentId) && !doomed.has(l.id)) {
            doomed.add(l.id);
            grew = true;
          }
        }
      }
      return ls.filter((l) => !doomed.has(l.id));
    });
    if (editingId === id) setEditingId(null);
  };

  const renderLevel = (node: LevelNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const open = !collapsed.has(node.id);
    const isEditing = editingId === node.id;
    return (
      <div key={node.id}>
        <div className="group flex items-center gap-2 py-1.5">
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleCollapse(node.id)}
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              <CaretIcon
                className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')}
              />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}

          {isEditing ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') setEditingId(null);
              }}
              className="h-8 flex-1"
            />
          ) : (
            <button
              type="button"
              onClick={() => hasChildren && toggleCollapse(node.id)}
              className={[
                'flex-1 text-left text-sm',
                depth === 0 ? 'font-semibold text-ink' : 'font-normal text-ink-secondary',
              ].join(' ')}
            >
              {node.name}
            </button>
          )}

          <div className="flex items-center gap-3 text-[#cbd5e1] opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              aria-label={`Edit ${node.name}`}
              onClick={() => startEdit(node)}
              className="transition-colors hover:text-brand"
            >
              <EditLineIcon className="size-4" />
            </button>
            <button
              type="button"
              aria-label={`Delete ${node.name}`}
              onClick={() => deleteLevel(node.id)}
              className="transition-colors hover:text-red-500"
            >
              <TrashIcon className="size-4" />
            </button>
          </div>
        </div>

        {hasChildren && open && (
          <div className="ml-2 flex flex-col gap-1 border-l border-line-soft pl-4">
            {node.children.map((child) => renderLevel(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Left — selection from step 2 (falls back to the EC reference tree) */}
      <Card>
        {selection && selection.length > 0 ? (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">Selected Constituency Hierarchy</h2>
            <div className="mt-6">
              <SelectedHierarchy nodes={selection} />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">
              Election Commission Level Hierarchy
            </h2>
            <div className="mt-6">
              <EcHierarchyTree rootLabel="Alwar Assembly Constituency" />
            </div>
          </>
        )}
      </Card>

      {/* Right — editable organization levels */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-bold leading-[22px] text-ink">Organization Level Hierarchy</h2>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex shrink-0 items-center gap-2 rounded-lg border-2 border-brand px-3 py-1.5 text-brand transition-colors hover:bg-brand-shade"
          >
            <span className="text-lg font-bold leading-none">+</span>
            <span className="text-left text-[10px] font-bold uppercase leading-[13px]">
              Add New
              <br />
              Level
            </span>
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-1 text-sm">
          {tree.map((node) => renderLevel(node, 0))}

          {levels.length === 0 && (
            <p className="rounded-2xl border border-dashed border-line py-10 text-center text-sm text-ink-secondary">
              No levels yet. Use “Add New Level” to start building the hierarchy.
            </p>
          )}
        </div>
      </Card>

      <AddLevelModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={createLevel}
        parents={parentOptions}
      />
    </div>
  );
}
